const MINI = 90000 * 5
/**
 * 并发下载数据
 * @param {*} cos
 */
async function loadDataByConcurrency(cos, config, firstPromise) {
  const TOTAL_LENGTH = config.totalLength || 0
  const PARTICAL = Math.ceil(TOTAL_LENGTH / 6)
  let start = MINI + 1,
    end = PARTICAL + MINI
  let promises = [firstPromise],
    promise = null

  while (start < TOTAL_LENGTH) {
    promise = new Promise((resolve, reject) => {
      cos.getObject(
        {
          Bucket: config.bucket,
          Region: config.region,
          Key: config.allowPrefix,
          DataType: "arraybuffer",
          Range: `bytes=${start}-${Math.min(end, TOTAL_LENGTH)}`,
          ResponseCacheControl: "no-store"
        },
        (err, data) => {
          if (err) {
            reject(err)
          }
          resolve(data)
        }
      )
    })

    promises.push(promise)
    start = end + 1
    end = end + PARTICAL
  }

  let result = new Promise((resolve, reject) => {
    Promise.all(promises)
      .then(rs => {
        let blob = new Blob(
          rs.map(it => it.Body),
          { type: rs[0].headers["content-type"] }
        )

        resolve(URL.createObjectURL(blob))
      })
      .catch(err => reject(err))
  })

  return result
}

/**
 * 串行下载数据
 * @param {*} cos
 */
async function loadDataBySerial(cos, config, start, end, success, fail, instance) {
  const TOTAL_LENGTH = config.totalLength
  const PARTICAL = Math.ceil(TOTAL_LENGTH / 6)
  cos.getObject(
    {
      Bucket: config.bucket,
      Region: config.region,
      Key: config.allowPrefix,
      DataType: "arraybuffer",
      Range: `bytes=${start}-${Math.min(end, TOTAL_LENGTH)}`,
      ResponseCacheControl: "no-store"
    },
    (err, data) => {
      if (err) {
        console.error(err)
        loadWholeAudio(cos, config, success, fail)
        return
      }
      instance.dataCache.push(data.Body)
      let blob = new Blob(instance.dataCache, { type: data.headers["content-type"] })
      success(URL.createObjectURL(blob), {
        ...config,
        buffer: instance.dataCache
      })
      if (end < TOTAL_LENGTH) {
        loadDataBySerial(cos, config, end + 1, end + Math.max(MINI, PARTICAL), success, fail, instance)
      }
    }
  )
}

/**
 * 完整下载
 */
function loadWholeAudio(cos, config, success, fail) {
  const TOTAL_LENGTH = config.totalLength
  cos.getObject(
    {
      Bucket: config.bucket,
      Region: config.region,
      Key: config.allowPrefix,
      DataType: "arraybuffer",
      Range: `bytes=0-${TOTAL_LENGTH}`,
      ResponseCacheControl: "no-store"
    },
    (err, data) => {
      if (err) {
        fail(err)
        return
      }
      let blob = new Blob([data.Body], { type: data.headers["content-type"] })
      success(URL.createObjectURL(blob), {
        ...config,
        buffer: [data.Body]
      })
    }
  )
}

/**
 * 加载第一段音频数据
 * @param {*} cos
 * @param {*} config
 */
function loadFirstSectionData(cos, config, instance) {
  const TOTAL_LENGTH = config.totalLength || 0
  const MINI_PARTICAL = MINI

  let promise = new Promise((resolve, reject) => {
    cos.getObject(
      {
        Bucket: config.bucket,
        Region: config.region,
        Key: config.allowPrefix,
        DataType: "arraybuffer",
        Range: `bytes=0-${Math.min(MINI_PARTICAL, TOTAL_LENGTH)}`,
        ResponseCacheControl: "no-store"
      },
      (err, data) => {
        if (err) {
          if (err.code.toString() !== "404") {
            instance.wholeDataPromise = normalLoadData(cos, config)
          } else {
            instance.wholeDataPromise = Promise.reject(err)
          }
          reject(err)
        }
        resolve(data)
      }
    )
  })

  instance.firstPromise = promise

  return new Promise((resolve, reject) => {
    promise
      .then(data => {
        let blob = new Blob([data.Body], { type: data.headers["content-type"] })
        let url = URL.createObjectURL(blob)
        resolve({
          url,
          finished: MINI_PARTICAL >= TOTAL_LENGTH
        })
      })
      .catch(err => reject(err))
  })
}

function normalLoadData(cos, config) {
  let promise = new Promise((resolve, reject) => {
    cos.getObject(
      {
        Bucket: config.bucket /* 必须 */,
        Region: config.region /* 存储桶所在地域，必须字段 */,
        Key: config.allowPrefix /* 必须 */,
        DataType: "arraybuffer"
      },
      function(err, data) {
        if (err) {
          reject(err)
          return
        }
        let blob = new Blob([data.Body], {
          type: data.headers["content-type"]
        })
        resolve(URL.createObjectURL(blob))
      }
    )
  })
  return promise
}

export default class AudioService {
  constructor(options = {}) {
    const defaultOptions = {}
    this.options = Object.assign({}, defaultOptions, options)
    this.firstPromise = Promise.resolve()
    this.allPromise = Promise.resolve()
    this.wholeDataPromise = Promise.resolve()
    this.cos = undefined
    this.dataCache = []
  }

  async getFirstSection() {
    const { expiredTime, credentials, config } = this.options
    // eslint-disable-next-line no-undef
    var cos = new COS({
      getAuthorization(options, callback) {
        callback({
          TmpSecretId: credentials.tmpSecretId, // 临时密钥的 tmpSecretId
          TmpSecretKey: credentials.tmpSecretKey, // 临时密钥的 tmpSecretKey
          SecurityToken: credentials.sessionToken, // 临时密钥的 sessionToken
          ExpiredTime: expiredTime
        })
      }
    })
    this.cos = cos

    return loadFirstSectionData(cos, config, this)
  }

  getAllSectionData() {
    const { config } = this.options
    if (MINI >= config.totalLength) {
      // 如果只有一个分片 返回空 promise
      return Promise.resolve()
    }
    return loadDataByConcurrency(this.cos, config, this.firstPromise)
  }

  serialLoadSection(success, fail) {
    const { expiredTime, credentials, config } = this.options
    const TOTAL_LENGTH = config.totalLength
    let end = Math.min(MINI, TOTAL_LENGTH)

    // eslint-disable-next-line no-undef
    var cos = new COS({
      getAuthorization(options, callback) {
        callback({
          TmpSecretId: credentials.tmpSecretId, // 临时密钥的 tmpSecretId
          TmpSecretKey: credentials.tmpSecretKey, // 临时密钥的 tmpSecretKey
          SecurityToken: credentials.sessionToken, // 临时密钥的 sessionToken
          ExpiredTime: expiredTime
        })
      }
    })
    loadDataBySerial(cos, config, 0, end, success, fail, this)
  }
}
